"use client";

import { useEffect, useRef } from "react";
import { buildLeadConversionPayload } from "@/lib/conversions";
import { rememberedCampaignAttribution } from "@/lib/campaign-attribution";
import { tracking } from "@/lib/tracking";
import type { TrackingDataLayerItem } from "@/lib/tracking";
import { analyticsEnabled } from "@/lib/analytics-preferences";

declare global {
  interface Window {
    dataLayer?: TrackingDataLayerItem[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Fires after enabled tags initialize, once per lead in this tab. */
export function LeadConversion({ source, reference }: { source: string; reference: string }) {
  const fired = useRef(false);

  useEffect(() => {
    if (!reference) return;

    const send = () => {
      if (fired.current || typeof window.gtag !== "function") return;
      if (!analyticsEnabled()) return;

      const sentKey = `dbm-ga4-lead:${reference}`;
      try {
        if (window.sessionStorage.getItem(sentKey)) return;
      } catch {
        // Enabled tracking can still send the event when storage is unavailable.
      }

      const payload = buildLeadConversionPayload(source, reference);
      const attribution = rememberedCampaignAttribution(window.location.href);
      window.dataLayer = window.dataLayer ?? [];
      window.dataLayer.push({ ...payload, ...attribution });
      window.gtag("event", "generate_lead", {
        send_to: tracking.ga4MeasurementId,
        lead_source: payload.lead_source,
        conversion_type: payload.conversion_type,
        transaction_id: payload.lead_reference,
        lead_landing_page: attribution.landing_page,
        lead_utm_source: attribution.utm_source,
        lead_utm_medium: attribution.utm_medium,
        lead_utm_campaign: attribution.utm_campaign,
        lead_utm_term: attribution.utm_term,
        lead_utm_content: attribution.utm_content,
        lead_utm_id: attribution.utm_id,
        lead_utm_source_platform: attribution.utm_source_platform,
        lead_utm_creative_format: attribution.utm_creative_format,
        lead_utm_marketing_tactic: attribution.utm_marketing_tactic,
      });
      fired.current = true;
      try {
        window.sessionStorage.setItem(sentKey, "1");
      } catch {
        // The in-memory guard still prevents duplicates while this page is mounted.
      }
    };

    window.addEventListener("dbm-ga4-ready", send);
    window.addEventListener("dbm-analytics-consent-change", send);
    send();

    return () => {
      window.removeEventListener("dbm-ga4-ready", send);
      window.removeEventListener("dbm-analytics-consent-change", send);
    };
  }, [reference, source]);

  return null;
}
